const THREE = window.THREE;
if (!THREE) {
  console.warn('[luxe-vici-3d] three.js unavailable — hero left as-is');
}
const { GLTFLoader, DRACOLoader, RectAreaLightUniformsLib, RoomEnvironment } = THREE || {};

const RIGS = {
  veni: {
    exposure: 1.0,
    envSigma: 0.035,
    ambient: { color: 0xffe8cf, intensity: 0.08 },
    key:     { color: 0xffe4c4, intensity: 22,  w: 1.1, h: 0.8, pos: [0, 2.5, 1.0] },
    fill:    { color: 0xfff0e0, intensity: 0.5, w: 1.6, h: 1.6, pos: [0, -0.1, 1.8] },
    rimLeft:  { color: 0xffd9ad, intensity: 13, dist: 6, angle: Math.PI / 10, penumbra: 0.35, decay: 1.1, pos: [-2.0, 1.2, -1.4], target: [-0.6, 0, 0] },
    rimRight: { color: 0xffd9ad, intensity: 13, dist: 6, angle: Math.PI / 10, penumbra: 0.35, decay: 1.1, pos: [2.0, 1.0, -1.0], target: [0.6, 0, 0] },
    crown:   { color: 0xffcf9e, intensity: 12.5, dist: 3, decay: 2, pos: [1.3, 0.5, 0.9] },
    strap:   { color: 0xffe9d5, intensity: 22, dist: 5, angle: Math.PI / 16, penumbra: 0.4, decay: 1.5, pos: [0, 3.2, 0.4], target: [0, 1.6, 0.5] }
  },
  vidi: {
    exposure: 1.15,
    envSigma: 0.05,
    ambient: { color: 0xfff2e0, intensity: 0.18 },
    key:     { color: 0xffe9d2, intensity: 18,  w: 1.5, h: 1.1, pos: [0, 2.4, 1.0] },
    fill:    { color: 0xfff8f0, intensity: 4.5, w: 1.8, h: 1.8, pos: [0, -0.1, 1.8] },
    rimLeft:  { color: 0xffd9b3, intensity: 9, dist: 6, angle: Math.PI / 9, penumbra: 0.4, decay: 1.2, pos: [-2.0, 1.2, -1.4], target: [-0.6, 0, 0] },
    rimRight: { color: 0xffd9b3, intensity: 9, dist: 6, angle: Math.PI / 9, penumbra: 0.4, decay: 1.2, pos: [2.0, 1.0, -1.0], target: [0.6, 0, 0] },
    crown:   { color: 0xffddb0, intensity: 3.5, dist: 3, decay: 2, pos: [1.3, 0.5, 0.9] },
    strap:   { color: 0xfff5e8, intensity: 5, dist: 5, angle: Math.PI / 12, penumbra: 0.6, decay: 1.4, pos: [0, 3.2, 0.4], target: [0, 1.6, 0.5] }
  },
  vici: {
    exposure: 0.9,
    envSigma: 0.04,
    ambient: { color: 0xffffff, intensity: 0.12 },
    key:     { color: 0xffffff, intensity: 20,  w: 1.4, h: 1.0, pos: [0, 2.4, 1.0] },
    fill:    { color: 0xffffff, intensity: 1.2, w: 1.6, h: 1.6, pos: [0, -0.2, 1.8] },
    rimLeft:  { color: 0xffffff, intensity: 12, dist: 6, angle: Math.PI / 9, penumbra: 0.4, decay: 1.2, pos: [-2.0, 1.2, -1.4], target: [-0.6, 0, 0] },
    rimRight: { color: 0xfff1de, intensity: 10, dist: 6, angle: Math.PI / 9, penumbra: 0.4, decay: 1.2, pos: [2.0, 1.0, -1.0], target: [0.6, 0, 0] },
    crown:   { color: 0xffdca8, intensity: 9, dist: 2, decay: 2, pos: [1.3, 0.5, 0.9] },
    strap:   { color: 0xffffff, intensity: 30, dist: 5, angle: Math.PI / 14, penumbra: 0.5, decay: 1.5, pos: [0, 3.2, 0.4], target: [0, 1.6, 0.5] }
  }
};

const DRACO_DECODER_PATH = 'https://cdn.jsdelivr.net/npm/three@0.169.0/examples/jsm/libs/draco/';

(function () {
  'use strict';

  const stage = document.querySelector('[data-vici-stage]');
  const canvas = stage && stage.querySelector('[data-vici-canvas]');
  const cue = stage && stage.querySelector('[data-vici-cue]');
  const toggle = document.querySelector('[data-vici-toggle]');
  if (!stage || !canvas) return;
  if (!THREE) return;
  const grab = stage.querySelector('[data-vici-grab]') || stage;

  const src = stage.getAttribute('data-glb-src');
  if (!src) {
    console.warn('[luxe-vici-3d] missing data-glb-src');
    return;
  }


  const RIG = (function () {
    const explicit = (stage.getAttribute('data-light-rig') || '').toLowerCase();
    if (RIGS[explicit]) return RIGS[explicit];
    const m = /(veni|vidi|vici)/i.exec(src);
    return RIGS[m ? m[1].toLowerCase() : 'vici'];
  })();

  const zoom = (function () {
    const z = parseFloat(stage.getAttribute('data-glb-zoom'));
    return isNaN(z) || z <= 0 ? 1 : z;
  })();

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const TARGET_SIZE = 2.0;
  const FIT_MARGIN  = 1.55;
  const FOV         = 32;
  const DROP        = 0.06;
  const EASE        = 0.09;
  const AUTO_SPEED  = 0.0022;
  const DRAG_SPEED  = 0.0065;
  const TILT_LIMIT  = 0.95;
  const PORTRAIT_EXPONENT = 0.75;
  const PORTRAIT_ZOOM_MAX = 1.75;
  const MOBILE_BREAKPOINT = 820;
  const MOBILE_ZOOM       = 1.18;
  const MOBILE_ZOOM_MAX   = 1.85;
  const DESKTOP_ZOOM      = 1.30;
  const DESKTOP_ZOOM_MAX  = 1.50;
  const RETURN_EASE = 0.06;
  const TWO_PI      = Math.PI * 2;

  const REVEAL_DURATION = 2.4;
  const REVEAL_TURN     = 0.75;
  const REVEAL_SCALE_IN = 0.94;

  function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }

  let renderer, scene, camera, pivot, envTexture, envTarget, pmremGenerator, rigGroup, raf = 0;
  let rigLights = {};

  let interactive = false, baseScale = 1, modelRadius = 1, restY = 0;
  let motionPaused = true;
  let settling = false;
  let revealDone = false;

  let onScreen = true, pageVisible = true;

  function shouldRun() { return onScreen && pageVisible; }

  function syncLoop() {
    if (shouldRun()) {
      if (!raf) raf = requestAnimationFrame(tick);
    } else if (raf) {
      cancelAnimationFrame(raf);
      raf = 0;
    }
  }

  let curX = 0, curY = 0, targetX = 0, targetY = 0, velX = 0, velY = 0;
  let dragging = false, lastPX = 0, lastPY = 0;

  const NO_TASK = { report() {}, done() {} };
  function bootTask(label, weight) {
    return window.CzardBoot ? window.CzardBoot.task(label, weight) : NO_TASK;
  }

  const taskPmrem = bootTask('hero-pmrem', 10);
  const taskModel = bootTask('hero-model', 46);
  const taskSetup = bootTask('hero-setup', 8);
  const taskCompile = bootTask('hero-compile', 18);

  const allTasks = [taskPmrem, taskModel, taskSetup, taskCompile];

  function reportBytes(task) {
    return (e) => {
      if (e && e.lengthComputable && e.total > 0) task.report(e.loaded / e.total);
    };
  }

  init()
    .catch((e) => {
      console.warn('[luxe-vici-3d] init failed, hero left as-is:', e);
      cleanup();
    })
    .finally(() => {
      allTasks.forEach((t) => t.done());
    });

  async function init() {
    renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance'
    });
    renderer.setClearAlpha(0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = RIG.exposure;
    renderer.outputColorSpace = THREE.SRGBColorSpace;

    if (RectAreaLightUniformsLib) RectAreaLightUniformsLib.init();

    scene = new THREE.Scene();
    scene.background = null;
    scene.fog = null;

    camera = new THREE.PerspectiveCamera(FOV, 1, 0.01, 100);
    camera.position.set(0, 0, 5);
    sizeToStage();

    pivot = new THREE.Group();
    scene.add(pivot);
    sizeToStage();

    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath(DRACO_DECODER_PATH);
    dracoLoader.preload();

    const loader = new GLTFLoader();
    loader.setDRACOLoader(dracoLoader);

    const envReady = setupEnvironment();
    const modelReady = loader.loadAsync(src, reportBytes(taskModel));
    modelReady.catch(() => {});

    await envReady;
    setupLightingDebugger();

    try {
      const gltf = await modelReady;
      taskModel.done();
      await onModel(gltf.scene);
    } catch (err) {
      console.error('[luxe-vici-3d] GLB failed to load:', err);
      cleanup();
      return;
    } finally {
      dracoLoader.dispose();
    }

    window.addEventListener('resize', onResize, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        onScreen = entries[0].isIntersecting;
        syncLoop();
      }, { threshold: 0.02 });
      io.observe(stage);
    }

    addPointerHandlers();
    setupMotionToggle();
    syncLoop();
  }


  async function setupEnvironment() {
    try {
      pmremGenerator = new THREE.PMREMGenerator(renderer);

      if (envTarget) { envTarget.dispose(); envTarget = null; }
      const room = new RoomEnvironment();
      envTarget = pmremGenerator.fromScene(room, RIG.envSigma);
      envTexture = envTarget.texture;
      scene.environment = envTexture;
      if (room.dispose) room.dispose();
    } catch (e) {
      console.warn('[luxe-vici-3d] environment unavailable — reflections disabled:', e);
      scene.environment = null;
    }

    scene.background = null;
    buildRig();
    taskPmrem.done();
  }

  function buildRig() {
    if (rigGroup) {
      scene.remove(rigGroup);
      rigGroup.traverse((node) => {
        if (node.isLight && node.dispose) node.dispose();
      });
      rigGroup = null;
    }
    rigGroup = new THREE.Group();
    rigLights = {};

    const c = RIG;

    rigLights.ambient = new THREE.AmbientLight(c.ambient.color, c.ambient.intensity);
    rigGroup.add(rigLights.ambient);

    function area(name, cfg) {
      const l = new THREE.RectAreaLight(cfg.color, cfg.intensity, cfg.w, cfg.h);
      rigLights[name] = l;
      return l;
    }

    function spot(name, cfg) {
      const l = new THREE.SpotLight(cfg.color, cfg.intensity, cfg.dist, cfg.angle, cfg.penumbra, cfg.decay);
      rigGroup.add(l.target);
      rigLights[name] = l;
      return l;
    }

    function point(name, cfg) {
      const l = new THREE.PointLight(cfg.color, cfg.intensity, cfg.dist, cfg.decay);
      rigLights[name] = l;
      return l;
    }

    function place(light, cfg) {
      light.position.fromArray(cfg.pos || [0, 0, 0]);
      if (cfg.target && light.target) light.target.position.fromArray(cfg.target);
      return light;
    }

    rigGroup.add(place(area('key', c.key), c.key));
    rigGroup.add(place(area('fill', c.fill), c.fill));
    rigGroup.add(place(spot('rimLeft', c.rimLeft), c.rimLeft));
    rigGroup.add(place(spot('rimRight', c.rimRight), c.rimRight));
    rigGroup.add(place(point('crown', c.crown), c.crown));
    rigGroup.add(place(spot('strap', c.strap), c.strap));

    scene.add(rigGroup);
  }

  function isLightingDebugEnabled() {
    return new URLSearchParams(window.location.search).has('light-debug') ||
      window.localStorage.getItem('czard-light-debug') === '1';
  }

  function mergeRig(target, source) {
    Object.keys(source).forEach((key) => {
      const value = source[key];
      if (value && typeof value === 'object' && !Array.isArray(value) && target[key]) {
        mergeRig(target[key], value);
      } else {
        target[key] = value;
      }
    });
  }

  function setupLightingDebugger() {
    if (!isLightingDebugEnabled()) return;

    const style = document.createElement('style');
    style.textContent = '.luxe-light-debugger{position:fixed;z-index:2147483647;right:16px;bottom:16px;width:min(390px,calc(100vw - 32px));padding:14px;background:#151515;color:#f7f2e9;border:1px solid #806c54;border-radius:8px;box-shadow:0 16px 45px rgba(0,0,0,.35);font:12px/1.4 ui-monospace,SFMono-Regular,Menlo,monospace}.luxe-light-debugger strong{display:block;font:600 14px/1.2 system-ui,sans-serif}.luxe-light-debugger p{margin:6px 0 10px;color:#c7bcad}.luxe-light-debugger__controls{max-height:310px;overflow:auto;margin:10px 0;border:1px solid #5f574d;border-radius:4px;padding:4px}.luxe-light-debugger details{border-bottom:1px solid #39342d;padding:5px 2px}.luxe-light-debugger details:last-child{border:0}.luxe-light-debugger summary{cursor:pointer;color:#f0d9ba}.luxe-light-debugger__field{display:grid;grid-template-columns:1fr 115px;align-items:center;gap:8px;margin:5px 3px}.luxe-light-debugger input{box-sizing:border-box;width:100%;min-width:0;background:#090909;color:#d6f5d6;border:1px solid #5f574d;border-radius:3px;padding:4px;font:11px ui-monospace,SFMono-Regular,Menlo,monospace}.luxe-light-debugger input[type=color]{height:28px;padding:1px}.luxe-light-debugger textarea{box-sizing:border-box;width:100%;height:210px;resize:vertical;padding:9px;background:#090909;color:#d6f5d6;border:1px solid #5f574d;border-radius:4px;font:11px/1.35 ui-monospace,SFMono-Regular,Menlo,monospace}.luxe-light-debugger button{margin:9px 7px 0 0;padding:6px 9px;background:#eee2cf;color:#17130e;border:0;border-radius:3px;cursor:pointer}.luxe-light-debugger__close{position:absolute;top:4px;right:3px;margin:0!important;background:transparent!important;color:#f7f2e9!important;font-size:18px}.luxe-light-debugger output{display:block;min-height:17px;margin-top:6px;color:#b6e0ad}';
    document.head.appendChild(style);

    const panel = document.createElement('aside');
    panel.className = 'luxe-light-debugger';
    panel.innerHTML = [
      '<button type="button" class="luxe-light-debugger__close" aria-label="Close lighting debugger">×</button>',
      '<strong>3D lighting debugger</strong>',
      '<p>Controls update the render live. Use JSON to share or paste a complete rig.</p>',
      '<div class="luxe-light-debugger__controls"></div>',
      '<textarea spellcheck="false" aria-label="Lighting configuration"></textarea>',
      '<div><button type="button" data-action="apply">Apply</button><button type="button" data-action="copy">Copy config</button></div>',
      '<output aria-live="polite"></output>'
    ].join('');
    document.body.appendChild(panel);

    const textarea = panel.querySelector('textarea');
    const output = panel.querySelector('output');
    const controls = panel.querySelector('.luxe-light-debugger__controls');
    const refresh = () => { textarea.value = JSON.stringify(RIG, null, 2); };
    let rebuildFrame = 0;

    const renderLive = () => {
      if (rebuildFrame) return;
      rebuildFrame = requestAnimationFrame(() => {
        rebuildFrame = 0;
        renderer.toneMappingExposure = Number(RIG.exposure) || 1;
        buildRig();
        renderer.render(scene, camera);
        refresh();
      });
    };

    const setAtPath = (path, value) => {
      let target = RIG;
      for (let i = 0; i < path.length - 1; i += 1) target = target[path[i]];
      target[path[path.length - 1]] = value;
    };

    const addField = (parent, label, value, path, color) => {
      const row = document.createElement('label');
      row.className = 'luxe-light-debugger__field';
      const name = document.createElement('span');
      name.textContent = label;
      const input = document.createElement('input');
      input.type = color ? 'color' : 'number';
      input.step = color ? undefined : 'any';
      input.value = color ? '#' + value.toString(16).padStart(6, '0') : value;
      input.addEventListener('input', () => {
        const next = color ? parseInt(input.value.slice(1), 16) : Number(input.value);
        if (!Number.isFinite(next)) return;
        setAtPath(path, next);
        renderLive();
      });
      row.append(name, input);
      parent.appendChild(row);
    };

    const renderControls = () => {
      controls.innerHTML = '';
      Object.keys(RIG).forEach((group) => {
        const value = RIG[group];
        if (typeof value === 'number') {
          addField(controls, group, value, [group], false);
          return;
        }
        const details = document.createElement('details');
        details.open = group === 'key';
        const summary = document.createElement('summary');
        summary.textContent = group;
        details.appendChild(summary);
        Object.keys(value).forEach((property) => {
          const current = value[property];
          if (property === 'color') addField(details, property, current, [group, property], true);
          else if (Array.isArray(current)) {
            ['x', 'y', 'z'].forEach((axis, index) => addField(details, property + '.' + axis, current[index], [group, property, index], false));
          } else if (typeof current === 'number') addField(details, property, current, [group, property], false);
        });
        controls.appendChild(details);
      });
    };
    refresh();
    renderControls();

    panel.querySelector('[data-action="apply"]').addEventListener('click', () => {
      try {
        const next = JSON.parse(textarea.value);
        mergeRig(RIG, next);
        renderer.toneMappingExposure = Number(RIG.exposure) || 1;
        setupEnvironment();
        output.textContent = 'Applied.';
        refresh();
        renderControls();
      } catch (error) {
        output.textContent = 'Invalid JSON: ' + error.message;
      }
    });
    panel.querySelector('[data-action="copy"]').addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(JSON.stringify(RIG, null, 2));
        output.textContent = 'Copied.';
      } catch (error) {
        textarea.select();
        output.textContent = 'Select and copy the configuration manually.';
      }
    });
    panel.querySelector('.luxe-light-debugger__close').addEventListener('click', () => panel.remove());
  }


  function setupMotionToggle() {
    if (!toggle) return;
    if (reduce) { toggle.style.display = 'none'; return; }
    toggle.addEventListener('click', function () {
      setMotion(motionPaused);
    });
  }

  function setMotion(play) {
    motionPaused = !play;
    if (toggle) {
      toggle.classList.toggle('is-paused', motionPaused);
      toggle.setAttribute('aria-pressed', String(!motionPaused));
      toggle.setAttribute('aria-label', motionPaused ? 'Play rotation' : 'Pause rotation');
    }
    document.dispatchEvent(new CustomEvent('vici:motion', { detail: { playing: play } }));
  }

  async function onModel(model) {
    const box = new THREE.Box3().setFromObject(model);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    model.position.sub(center);

    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    baseScale = TARGET_SIZE / maxDim;
    modelRadius = (size.length() / 2) * baseScale;
    restY = -TARGET_SIZE * DROP;

    pivot.add(model);
    pivot.position.set(0, restY, 0);
    pivot.scale.setScalar(reduce ? baseScale : 0.0001);

    prepareMaterials(model);
    frameCamera();
    taskSetup.done();

    if (renderer.compileAsync) {
      try {
        await renderer.compileAsync(scene, camera);
      } catch (e) {
        console.warn('[luxe-vici-3d] shader precompile skipped:', e);
      }
    } else if (renderer.compile) {
      renderer.compile(scene, camera);
    }
    taskCompile.done();

    startReveal();
  }

  function prepareMaterials(model) {
    model.traverse((node) => {
      if (!node.isMesh || !node.material) return;
      const mats = Array.isArray(node.material) ? node.material : [node.material];

      mats.forEach((mat) => {
        try {
          if ('flatShading' in mat) mat.flatShading = false;

          if (mat.transmission > 0) {
            if ('thickness' in mat) mat.thickness = Math.min(mat.thickness || 0, 0.25);
          }

          mat.needsUpdate = true;
        } catch (e) {
        }
      });
    });
  }

  function frameCamera() {
    const vFov = THREE.MathUtils.degToRad(camera.fov);
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * camera.aspect);
    const fitFov = Math.min(vFov, hFov);

    let portrait = 1;
    if (hFov < vFov) {
      portrait = Math.min(Math.pow(vFov / hFov, PORTRAIT_EXPONENT), PORTRAIT_ZOOM_MAX);
    }
    if (window.innerWidth <= MOBILE_BREAKPOINT) {
      portrait = Math.min(portrait * MOBILE_ZOOM, MOBILE_ZOOM_MAX);
    } else {
      portrait = Math.min(portrait * DESKTOP_ZOOM, DESKTOP_ZOOM_MAX);
    }

    const dist = (modelRadius / Math.sin(fitFov / 2)) * (FIT_MARGIN / (zoom * portrait));
    camera.position.set(0, 0, dist);
    camera.near = Math.max(dist * 0.05, dist - modelRadius * 2.5);
    camera.far = dist + modelRadius * 2.5;
    camera.lookAt(0, restY, 0);
    camera.updateProjectionMatrix();
  }

  function sizeToStage() {
    if (!renderer || !camera) return;
    const w = stage.clientWidth || window.innerWidth;
    const h = stage.clientHeight || window.innerHeight;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }

  function onResize() {
    sizeToStage();
    if (pivot && pivot.children.length) frameCamera();
  }

  function onVisibility() {
    pageVisible = !document.hidden;
    syncLoop();
  }

  function startReveal() {
    whenIntro(function () {
      const gsap = window.gsap;
      if (reduce || !gsap) { plainReveal(); return; }
      swirlReveal(gsap);
    });
  }

  function swirlReveal(gsap) {
    if (window.CustomEase && !window.CustomEase.get('luxeSwirl')) {
      window.CustomEase.create('luxeSwirl', 'M0,0 C0.16,0.84 0.1,1 1,1');
    }
    const ease = window.CustomEase && window.CustomEase.get('luxeSwirl') ? 'luxeSwirl' : 'power3.out';

    const startAngle = -REVEAL_TURN * TWO_PI;

    pivot.position.set(0, restY, 0);
    pivot.rotation.set(0, startAngle, 0);
    pivot.scale.setScalar(baseScale * REVEAL_SCALE_IN);

    const state = { p: 0 };
    const tl = gsap.timeline({ onComplete: finishReveal });

    tl.fromTo(canvas, { opacity: 0 }, { opacity: 1, duration: 1.2, ease: 'power2.out' }, 0);

    tl.to(state, {
      p: 1,
      duration: REVEAL_DURATION,
      ease: ease,
      onUpdate: function () {
        const p = state.p;
        pivot.rotation.y = startAngle * (1 - p);
        pivot.scale.setScalar(baseScale * (REVEAL_SCALE_IN + (1 - REVEAL_SCALE_IN) * p));
      }
    }, 0);

    grab.addEventListener('pointerdown', function skip() {
      grab.removeEventListener('pointerdown', skip);
      if (tl.isActive()) tl.progress(1);
    }, { once: true });
  }

  function plainReveal() {
    pivot.scale.setScalar(baseScale);
    pivot.position.set(0, restY, 0);
    pivot.rotation.set(reduce ? 0 : -0.12, reduce ? 0 : -0.5, 0);
    if (!reduce) {
      canvas.style.transition = 'opacity 1s ease';
      requestAnimationFrame(function () { canvas.style.opacity = '1'; });
    } else {
      canvas.style.opacity = '1';
    }
    finishReveal();
  }

  function finishReveal() {
    pivot.position.set(0, restY, 0);
    pivot.scale.setScalar(baseScale);
    pivot.rotation.z = 0;

    curX = targetX = pivot.rotation.x;
    curY = targetY = pivot.rotation.y;
    velX = velY = 0;

    canvas.style.opacity = '1';
    interactive = true;
    revealDone = true;
    showCue();
  }

  function showCue() {
    if (!cue) return;
    cue.classList.add('is-visible');
    window.setTimeout(function () { cue.classList.remove('is-visible'); }, 5200);
  }

  function addPointerHandlers() {
    grab.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }

  function onDown(e) {
    if (!interactive) return;
    dragging = true;
    settling = false;
    lastPX = e.clientX;
    lastPY = e.clientY;
    velX = velY = 0;
    grab.classList.add('is-grabbing');
    if (cue) cue.classList.remove('is-visible');
  }

  function onMove(e) {
    if (!dragging) return;
    const dx = e.clientX - lastPX;
    const dy = e.clientY - lastPY;
    lastPX = e.clientX;
    lastPY = e.clientY;
    velY = dx * DRAG_SPEED;
    velX = dy * DRAG_SPEED;
    targetY += velY;
    targetX += velX;
  }

  function onUp() {
    if (!dragging) return;
    dragging = false;
    settling = true;
    grab.classList.remove('is-grabbing');
  }

  function tick() {
    raf = 0;
    if (!shouldRun()) return;

    if (interactive) {
      if (!dragging) {
        if (settling) {
          const homeY = Math.round(targetY / TWO_PI) * TWO_PI;
          targetX += (0 - targetX) * RETURN_EASE;
          targetY += (homeY - targetY) * RETURN_EASE;
          if (Math.abs(targetX) < 0.001 && Math.abs(targetY - homeY) < 0.001 &&
              Math.abs(curX) < 0.002 && Math.abs(curY - homeY) < 0.002) {
            targetX = 0;
            targetY = homeY;
            settling = false;
          }
        } else if (!reduce && !motionPaused) {
          targetY += AUTO_SPEED;
        }
      }
      targetX = clamp(targetX, -TILT_LIMIT, TILT_LIMIT);
      curX += (targetX - curX) * EASE;
      curY += (targetY - curY) * EASE;
      pivot.rotation.x = curX;
      pivot.rotation.y = curY;
    }

    const staticScene = revealDone && !dragging && !settling && (motionPaused || reduce);
    if (!staticScene) renderer.render(scene, camera);

    raf = requestAnimationFrame(tick);
  }

  function whenIntro(cb) {
    if (window.__czardIntro) { cb(); return; }
    document.addEventListener('czard:intro', cb, { once: true });
  }

  function cleanup() {
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    if (rigGroup && scene) { scene.remove(rigGroup); rigGroup = null; }
    if (pmremGenerator) pmremGenerator.dispose();
    if (envTarget) envTarget.dispose();
    else if (envTexture) envTexture.dispose();
    if (renderer) renderer.dispose();
    if (stage) stage.setAttribute('data-vici-failed', '');
  }
})();
